import { useState, FormEvent, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import ThemeSwitcher from '../components/layout/ThemeSwitcher';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Fingerprint } from 'lucide-react';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, loginWithPasskey, user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [emailValue, setEmailValue] = useState('');

  // v0.4.9.2.3c10: direct navigation, NO redirect chain.
  //
  // The previous behavior (v0.4.6i) navigated to `/guard` for EVERY
  // user. For civil respondents (role='agent' or 'agentAdmin'),
  // `/guard` rejects them via `allowedRoles` and the PrivateRoute
  // redirects to `/`, which then redirects to `/civil-respondent`.
  // That's a 3-hop chain: /guard → / → /civil-respondent.
  //
  // B2B users (admin, sadmin, etc.) hit /guard and stay there
  // because their role IS in the allowed list — single hop.
  //
  // On Android WebView, the rapid chain of `replace: true`
  // navigations through react-router-dom v6 can leave the URL
  // pointing to a route that doesn't match anything, rendering
  // an empty <Routes> child. The body keeps its theme background
  // but no component renders → white/black screen.
  //
  // Fix: pick the right destination UP-FRONT, in LoginPage, based
  // on user_type. Single navigation. No chain. No chance of
  // getting stuck between redirects.
  useEffect(() => {
    if (user) {
      // v0.4.9.2.3c11: route by ROLE, not user_type. user_type is
      // missing from the passkey login response (server bug) but role
      // is always present. Routing by role fixes the flicker loop
      // for passkey users.
      const role = user.role;
      const isAgent = role === 'agent' || role === 'agentAdmin';
      const target = isAgent ? '/civil-respondent' : '/guard';
      console.log('[LoginPage] user logged in, navigating to', target,
        '(role:', role, ')');
      navigate(target, { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const result = await login(email, password);
    if (!result.ok) {
      if (result.error === 'mobile_app_required') {
        setError(t('login.mobileAppRequired'));
      } else if (result.error === 'session_exists_mobile') {
        setError(t('login.sessionExistsMobile'));
      } else if (result.error === 'session_exists_desktop') {
        setError(t('login.sessionExistsDesktop'));
      } else {
        setError(t('login.invalidCredentials'));
      }
    }
  };

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    await new Promise(r => setTimeout(r, 500));
    let lastResult: { ok: boolean; error?: string } | null = null;
    for (let attempt = 1; attempt <= 2; attempt++) {
      // v0.4.9.2.3c10: passkey login no longer takes an email arg
      // (the server's /api/auth/passkey/login/options endpoint doesn't
      // support email-hint, and AuthContext.loginWithPasskey is
      // declared as () => Promise<...>). Passing the email caused a
      // TS2554 error which made `tsc && vite build` fail, blocking
      // deployment of every c4–c9 fix. Steven was running stale
      // bundles from before the type mismatch was introduced.
      lastResult = await loginWithPasskey();
      if (lastResult.ok) return;
      if (lastResult.error === 'mobile_app_required' ||
          lastResult.error === 'session_exists_mobile' ||
          lastResult.error === 'session_exists_desktop' ||
          lastResult.error === 'passkey_cancelled') {
        break;
      }
      if (attempt === 2) break;
      await new Promise(r => setTimeout(r, 300));
    }
    if (lastResult && !lastResult.ok) {
      switch (lastResult.error) {
        case 'mobile_app_required':
          setError(t('login.mobileAppRequired'));
          break;
        case 'session_exists_mobile':
          setError(t('login.sessionExistsMobile'));
          break;
        case 'session_exists_desktop':
          setError(t('login.sessionExistsDesktop'));
          break;
        case 'passkey_cancelled':
          setError('');
          break;
        default:
          setError(t('login.passkeyFailedRetry') || t('login.passkeyFailed'));
      }
    }
    setPasskeyLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center justify-between px-4 sm:px-6 max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="iSentinel360" className="h-8 w-8" />
            <span className="font-bold text-base sm:text-lg">iSentinel360</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md w-96">
          <h1 className="text-2xl font-bold mb-6 text-center">{t('login.welcomeBack')}</h1>

          <Button
            onClick={handlePasskeyLogin}
            disabled={passkeyLoading}
            variant="outline"
            className="w-full gap-2 mb-4 whitespace-normal text-center leading-tight flex-wrap h-auto py-2.5"
          >
            <Fingerprint className="w-4 h-4 flex-shrink-0" />
            <span className="break-words">
              {passkeyLoading ? t('login.authenticating') : t('login.loginWithPasskey')}
            </span>
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white dark:bg-gray-800 px-2 text-muted-foreground">
                {t('login.orUsePassword')}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              name="email"
              type="email"
              placeholder={t('login.emailPlaceholder')}
              autoComplete="username"
              className="w-full p-2 border rounded bg-background"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              required
            />
            <input
              name="password"
              type="password"
              placeholder={t('login.passwordPlaceholder')}
              autoComplete="current-password"
              className="w-full p-2 border rounded bg-background"
              required
            />
            <Button type="submit" className="w-full">{t('login.loginWithPassword')}</Button>
          </form>

          {error && <p className="text-red-500 text-sm mt-2 text-center">{error}</p>}
        </div>
      </div>
    </div>
  );
}
